/**
 * CelebrationOverlay Component (Phase 21.3.5)
 *
 * Portal-rendered overlay that celebrates a newly-completed goal.
 * Renders into document.body at z-[62] (above BottomSheet z-[60],
 * below ContextualTooltip z-[65] and WelcomeScreen z-[70]).
 *
 * Auto-dismisses after 3.5 seconds. Tap backdrop or buttons to dismiss.
 * CSS-only animations: celebrationPulse, sparkle, badgePop, celebrationSlideUp.
 *
 * Respects prefers-reduced-motion via keyframe overrides in index.css.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { getScoreColor } from '../../../lib/score-colors';
import { getRubricLabel } from '../utils';
import type { OperatorGoalResponse } from '@guild/shared';

interface CelebrationOverlayProps {
  /** The newly-completed goal to celebrate, or null to hide */
  goal: OperatorGoalResponse | null;
  /** Called when the overlay should close (timer, backdrop tap, button) */
  onDismiss: () => void;
  /** Called when user taps "Set Your Next Goal" CTA */
  onSetNextGoal: () => void;
}

/** Sparkle dot positions around the checkmark circle (top%, left%) */
const SPARKLE_POSITIONS = [
  { top: '5%', left: '50%' },
  { top: '20%', left: '85%' },
  { top: '65%', left: '90%' },
  { top: '90%', left: '50%' },
  { top: '65%', left: '10%' },
  { top: '20%', left: '15%' },
] as const;

const SPARKLE_COLORS = [
  'bg-guild-green',
  'bg-guild-mint',
  'bg-emerald-400',
  'bg-guild-green',
  'bg-guild-mint',
  'bg-emerald-400',
] as const;

export function CelebrationOverlay({ goal, onDismiss, onSetNextGoal }: CelebrationOverlayProps) {
  const ctaRef = useRef<HTMLButtonElement>(null);

  // Auto-dismiss timer: 3.5 seconds
  useEffect(() => {
    if (!goal) return;
    const timer = setTimeout(onDismiss, 3500);
    return () => clearTimeout(timer);
  }, [goal, onDismiss]);

  // Focus the CTA button on mount for accessibility
  useEffect(() => {
    if (goal && ctaRef.current) {
      ctaRef.current.focus();
    }
  }, [goal]);

  // Escape key dismisses
  useEffect(() => {
    if (!goal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goal, onDismiss]);

  if (!goal) return null;

  const scoreText = goal.currentScore !== null ? getRubricLabel(Math.round(goal.currentScore)) : '--';
  const targetText = getRubricLabel(goal.targetScore);

  const overlay = (
    <div
      className="fixed inset-0 z-[62] flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
      role="presentation"
    >
      {/* Card */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label="Goal Achieved"
        className="mx-6 max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        style={{ animation: 'celebrationSlideUp 0.4s ease-out both' }}
      >
        {/* Checkmark with sparkles */}
        <div className="mx-auto mb-4 flex items-center justify-center">
          <div className="relative h-20 w-20">
            {/* Radial pulse behind the checkmark */}
            <div
              className="absolute inset-[-20px] rounded-full bg-guild-green"
              style={{
                animation: 'celebrationPulse 1.5s ease-out infinite',
                opacity: 0.15,
              }}
            />

            {/* Sparkle dots */}
            {SPARKLE_POSITIONS.map((pos, i) => (
              <div
                key={i}
                className={cn(
                  'absolute h-1.5 w-1.5 rounded-full',
                  SPARKLE_COLORS[i],
                )}
                style={{
                  top: pos.top,
                  left: pos.left,
                  animation: `sparkle 0.8s ease-out ${i * 0.1}s both`,
                }}
              />
            ))}

            {/* Checkmark icon */}
            <div
              className="flex h-full w-full items-center justify-center rounded-full bg-emerald-100"
              style={{ animation: 'badgePop 0.3s ease-out both' }}
            >
              <CheckCircle2
                size={40}
                strokeWidth={1.5}
                className="text-emerald-600"
              />
            </div>
          </div>
        </div>

        {/* Heading */}
        <h2 className="text-center text-xl font-bold text-op-text-primary">
          Goal Achieved!
        </h2>

        {/* SOP title */}
        <p className="mt-1 text-center text-sm text-op-text-secondary line-clamp-2">
          {goal.sopTitle}
        </p>

        {/* Score display */}
        <div className="mt-3 flex items-center justify-center gap-2">
          <span className={cn('text-lg font-bold', getScoreColor(goal.currentScore, 'text'))}>
            {scoreText}
          </span>
          <ArrowRight size={16} strokeWidth={2} className="text-op-text-tertiary" />
          <span className="text-sm font-semibold text-op-text-primary">
            {targetText}
          </span>
        </div>

        {/* Buttons */}
        <div className="mt-5 flex flex-col gap-2">
          <button
            ref={ctaRef}
            type="button"
            onClick={onSetNextGoal}
            className="min-h-[44px] rounded-xl bg-guild-green px-6 py-2.5 text-sm font-semibold text-white active:bg-guild-green/90 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            Set Your Next Goal
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="min-h-[44px] rounded-xl px-6 py-2.5 text-sm font-semibold text-op-text-secondary active:bg-gray-100 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
